/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_PRIMARY,
  ButtonProps,
  DIALOG_SIZE_SM_MD,
  DialogContentProps,
  DialogProps,
  ICON_EDIT,
  useDialogStore,
} from '@squiz/sds';
import { DialogLauncher } from 'apps/console-core/src/Components/DialogLauncher/DialogLauncher';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { EDIT_TOOL_FORM_IDS } from './EditToolDialog.constants';
import { EditToolDialogProps } from './EditToolDialog.props';
import { EditToolDialogForm } from './EditToolDialogForm/EditToolDialogForm';
import { isGenerative } from '../../Utilities/formPredicates';

// TODO: make generic for either updating content tool (type : 'generative)
// or updating chatbot (type: 'chatbot')
/**
 * The dialog for editing a content tool.
 * @param {EditToolDialogProps} props Props passed to `<EditToolDialog />`.
 * @returns {JSX.Element} The `<EditToolDialog />` component.
 */
export const EditToolDialog = (props: EditToolDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const canEditTool = hasPermission(
    isGenerative(props.type) ? PERMISSIONS.dxpAi.GENERATIVE_WRITE : PERMISSIONS.dxpAi.CHATBOT_WRITE,
  );

  if (!canEditTool) {
    return <></>;
  }

  const buttonProps: ButtonProps = {
    dataId: EDIT_TOOL_FORM_IDS.contentToolEditButton,
    format: BUTTON_FORMAT_STRONG,
    icon: ICON_EDIT,
    text: t(AI_STRINGS.edit),
    theme: BUTTON_THEME_PRIMARY,
  };

  const { contentTool } = props;

  const dialogProps: DialogProps = {
    dialogContent: {
      renderContent: (dialogProps: DialogContentProps): JSX.Element => {
        return (
          <EditToolDialogForm
            {...dialogProps}
            customProps={{ clearCache: props.clearCache, contentTool: contentTool }}
            type={props.type}
          />
        );
      },
    },
    dialogSize: DIALOG_SIZE_SM_MD,
    heading: isGenerative(contentTool.type)
      ? t(AI_STRINGS.editContentToolDialogHeading)
      : t(AI_STRINGS.editChatbotDialogHeading),
    icon: ICON_EDIT,
    showDividers: false,
    stateHandler: useDialogStore,
  };
  return (
    <div>
      <DialogLauncher buttonProps={buttonProps} dialogProps={dialogProps} />
    </div>
  );
};
